import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { cn } from "@lib/cn";
import { useLayoutEffect, type ReactNode } from "react";

export type CursorPoint = { x: number; y: number };

/**
 * A menu anchored to the pointer rather than to a control — what a right click
 * needs, and the one thing the shared Popover cannot do, since it takes its
 * reference from a trigger element.
 *
 * The anchor is a zero-size virtual rect at the click, so floating-ui still
 * flips and shifts the menu near a viewport edge; a row at the bottom of a long
 * list opens upwards instead of off screen.
 */
export const CursorMenu = ({
  point,
  onClose,
  children,
  className,
}: {
  /** Where the menu is anchored, or null when it is closed. */
  point: CursorPoint | null;
  onClose: () => void;
  children: ReactNode;
  /**
   * Stacking override. The default clears a page; inside a dialog, which stacks
   * from 10000, the menu has to be raised above it.
   */
  className?: string;
}) => {
  const open = point !== null;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (next) => {
      if (!next) onClose();
    },
    placement: "bottom-start",
    strategy: "fixed",
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  /**
   * A virtual anchor cannot be passed through `elements.reference` — that one is
   * typed for real DOM nodes — so it goes in through the ref setter. It has to
   * be a layout effect: a plain effect runs after paint, and a fixed-position
   * menu with no reference yet paints once in the top-left corner.
   */
  useLayoutEffect(() => {
    if (!point) return;
    refs.setPositionReference({
      getBoundingClientRect: () =>
        ({
          x: point.x,
          y: point.y,
          top: point.y,
          left: point.x,
          right: point.x,
          bottom: point.y,
          width: 0,
          height: 0,
        }) as DOMRect,
    });
  }, [point, refs]);

  const { getFloatingProps } = useInteractions([
    useDismiss(context, { outsidePress: true, escapeKey: true }),
    useRole(context, { role: "menu" }),
  ]);

  if (!open) return null;

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        className={cn("tw-z-[9999]", className)}
        // The row underneath toggles selection on click and opens on double
        // click; neither should fire from inside the menu.
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        // Both, not just preventDefault: this is a portal, so the synthetic
        // event still bubbles up the React tree to the row, which would
        // re-anchor the menu under the new pointer position.
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        {...getFloatingProps()}
      >
        {children}
      </div>
    </FloatingPortal>
  );
};
